import { map, trim, compact } from "lodash";
import { useQuery, useMutation } from "react-query";
import { keycloakapiUrls } from "misc/urls";

import { queryClient } from "queryClient";

export const useUsers = (queryProps, params) => {
    const { data = [], ...usersQueryState } = useQuery(["users"], {
        meta: {
            request: {
                method: "GET",
                url: keycloakapiUrls.users(),
                params: {
                    ...params
                }
            }
        },
        select: data =>
            map(data, user => ({
                ...user,
                fullName: trim(compact([user.firstName, user.lastName]).join(" ")) || ""
            })),
        refetchOnMount: true,
        ...queryProps
    });
    const createUserMutation = useMutation();
    const updateUserMutation = useMutation();
    const deleteUserMutation = useMutation();
    const resetPasswordMutation = useMutation();

    const createUser = (user, { onSuccess, ...props } = {}) => {
        createUserMutation.mutate(
            {
                method: "POST",
                url: keycloakapiUrls.users(),
                body: JSON.stringify({
                    email: user.email,
                    firstName: user.firstName,
                    lastName: user.lastName,
                    locale: user.locale,
                    permissions: user.permissions,
                    ...(user.organizationId && { organizationId: user.organizationId })
                })
            },
            {
                onSuccess: newData => {
                    queryClient.setQueryData("users", oldData => [...oldData, newData]);
                    queryClient.invalidateQueries(["customers"]);
                    queryClient.invalidateQueries(["roles"]);
                    onSuccess && onSuccess(newData);
                },
                ...props
            }
        );
    };

    const updateUser = (user, { onSuccess, ...props } = {}) => {
        updateUserMutation.mutate(
            {
                method: "PATCH",
                url: `${keycloakapiUrls.users()}${user.id}/`,
                body: JSON.stringify({
                    email: user.email,
                    firstName: user.firstName,
                    lastName: user.lastName,
                    locale: user.locale,
                    permissions: user.permissions,
                    ...(user.organizationId && { organizationId: user.organizationId })
                })
            },
            {
                onSuccess: newData => {
                    queryClient.setQueryData("users", oldData => oldData.map(user => (newData.id === user.id ? newData : user)));
                    queryClient.invalidateQueries(["customers"]);
                    queryClient.invalidateQueries(["roles"]);
                    onSuccess && onSuccess(newData);
                },
                ...props
            }
        );
    };

    const deleteUser = (user, { onSuccess, ...props } = {}) => {
        deleteUserMutation.mutate(
            {
                method: "DELETE",
                url: `${keycloakapiUrls.users()}${user.id}/`
            },
            {
                onSuccess: newData => {
                    queryClient.setQueryData("users", oldData => oldData.filter(user => user.id !== newData));
                    queryClient.invalidateQueries(["customers"]);
                    queryClient.invalidateQueries(["roles"]);
                    onSuccess && onSuccess();
                },
                ...props
            }
        );
    };

    const resetPassword = (userId, tempPassword, { onSuccess, onError, ...props } = {}) => {
        resetPasswordMutation.mutate(
            {
                method: "PUT",
                url: `${keycloakapiUrls.users()}${userId}/reset_password/`,
                body: JSON.stringify({
                    client: "usermanagement",
                    tempPassword: tempPassword
                })
            },
            {
                onSuccess: data => {
                    onSuccess && onSuccess(data);
                },
                onError: data => {
                    onError && onError(data);
                },
                ...props
            }
        );
    };

    return {
        users: data,
        usersQueryState,
        createUser,
        createUserMutation,
        updateUser,
        updateUserMutation,
        deleteUser,
        deleteUserMutation,
        resetPassword,
        resetPasswordMutation
    };
};

export const useRoles = ({ clients, queryProps } = {}) => {
    const { data = [], ...rolesQueryState } = useQuery(["roles"], {
        meta: {
            request: {
                method: "GET",
                url: keycloakapiUrls.roles(),
                params: { clients: clients }
            }
        },
        refetchOnMount: true,
        ...queryProps
    });

    return { roles: data, ...rolesQueryState };
};

export const useCreateRole = () => {
    const createRoleMutation = useMutation({
        onError: error => {
            console.log("Error creating role:", error);
        }
    });

    const createRole = (role, { onSuccess, onError, ...props } = {}) => {
        createRoleMutation.mutate(
            {
                method: "POST",
                url: keycloakapiUrls.roles(),
                body: JSON.stringify({
                    client: role.client,
                    name: role.name,
                    description: role.description,
                    permissions: role.permissions,
                    organizationId: role.organizationId || null,
                    attributes: role.attributes
                })
            },
            {
                onSuccess: newData => {
                    queryClient.setQueryData(["roles"], oldData => [...oldData, newData]);
                    queryClient.invalidateQueries(["users"]);
                    queryClient.invalidateQueries(["customers"]);
                    onSuccess && onSuccess(newData);
                },
                onError: error => {
                    onError && onError(error);
                },
                ...props
            }
        );
    };

    return { createRole, ...createRoleMutation };
};

export const useUpdateRole = () => {
    const updateRoleMutation = useMutation();

    const updateRole = (role, { onSuccess, ...props } = {}) => {
        updateRoleMutation.mutate(
            {
                method: "PATCH",
                url: `${keycloakapiUrls.roles()}${role.id}/`,
                body: JSON.stringify({
                    client: role.client,
                    name: role.name,
                    description: role.description,
                    permissions: role.permissions,
                    attributes: role.attributes
                })
            },
            {
                onSuccess: newData => {
                    queryClient.setQueryData(["roles"], oldData => oldData.map(role => (role.id === newData.id ? newData : role)));
                    queryClient.invalidateQueries(["users"]);
                    queryClient.invalidateQueries(["customers"]);
                    onSuccess && onSuccess();
                },
                ...props
            }
        );
    };

    return { updateRole, ...updateRoleMutation };
};

export const useDeleteRole = () => {
    const deleteRoleMutation = useMutation();

    const deleteRole = (role, { onSuccess, ...props } = {}) => {
        deleteRoleMutation.mutate(
            {
                method: "DELETE",
                url: `${keycloakapiUrls.roles()}${role.id}/`
            },
            {
                onSuccess: newData => {
                    queryClient.setQueryData(["roles"], oldData => oldData.filter(role => role.id !== newData));
                    queryClient.invalidateQueries(["users"]);
                    queryClient.invalidateQueries(["customers"]);
                    onSuccess && onSuccess();
                },
                ...props
            }
        );
    };

    return { deleteRole, ...deleteRoleMutation };
};

export const usePermissions = (queryProps, clients = []) => {
    const { data = [], ...query } = useQuery(["permissions", clients], {
        meta: {
            request: {
                method: "GET",
                url: keycloakapiUrls.permissions(),
                params: { client: clients.join(",") }
            }
        },
        ...queryProps
    });

    return { permissions: data, ...query };
};

export const useGroups = queryProps => {
    const { data = [], ...query } = useQuery(["groups"], {
        meta: { request: { method: "GET", url: keycloakapiUrls.groups() } },
        refetchOnMount: true,
        ...queryProps
    });

    return {
        groups: data,
        ...query
    };
};

export const useCreateGroup = () => {
    const { mutate, ...mutation } = useMutation();

    return {
        createGroup: (group, { onSuccess, ...props } = {}) => {
            mutate(
                {
                    method: "POST",
                    url: keycloakapiUrls.groups(),
                    body: JSON.stringify({
                        name: group.name,
                        users: group?.users,
                        permissions: group?.permissions
                    })
                },
                {
                    onSuccess: newData => {
                        queryClient.setQueryData("groups", oldData => [...oldData, newData]);
                        queryClient.invalidateQueries(["users"]);
                        onSuccess && onSuccess(newData);
                    },
                    ...props
                }
            );
        },
        ...mutation
    };
};
export const useUpdateGroup = () => {
    const { mutate, ...mutation } = useMutation();

    return {
        updateGroup: (group, { onSuccess, ...props } = {}) => {
            mutate(
                {
                    method: "PATCH",
                    url: `${keycloakapiUrls.groups()}${group.id}/`,
                    body: JSON.stringify({
                        name: group.name,
                        users: group?.users,
                        permissions: group?.permissions
                    })
                },
                {
                    onSuccess: newData => {
                        queryClient.setQueryData("groups", oldData => oldData.map(group => (newData.id === group.id ? newData : group)));
                        queryClient.invalidateQueries(["users"]);
                        onSuccess && onSuccess(newData);
                    },
                    ...props
                }
            );
        },
        ...mutation
    };
};
export const useDeleteGroup = () => {
    const { mutate, ...mutation } = useMutation();

    return {
        deleteGroup: (group, { onSuccess, ...props } = {}) => {
            mutate(
                {
                    method: "DELETE",
                    url: `${keycloakapiUrls.groups()}${group.id}/`
                },
                {
                    onSuccess: newData => {
                        queryClient.setQueryData("groups", oldData => oldData.filter(group => group.id !== newData));
                        queryClient.invalidateQueries(["users"]);
                        onSuccess && onSuccess();
                    },
                    ...props
                }
            );
        },
        ...mutation
    };
};

export const useCustomers = queryProps => {
    const { data: customers = [], ...customersQueryState } = useQuery(["customers"], {
        meta: {
            request: {
                method: "GET",
                url: keycloakapiUrls.customers()
            }
        },
        refetchOnMount: true,
        ...queryProps
    });

    const createCustomerMutation = useMutation();
    const updateCustomerMutation = useMutation();
    const deleteCustomerMutation = useMutation();

    const createCustomer = (customer, { onSuccess, ...props } = {}) => {
        createCustomerMutation.mutate(
            {
                method: "POST",
                url: keycloakapiUrls.customers(),
                body: JSON.stringify({
                    name: customer.name,
                    permissions: customer.permissions,
                    rootUser: customer.rootUser
                })
            },
            {
                onSuccess: newData => {
                    queryClient.setQueryData("customers", oldData => [...oldData, newData]);
                    queryClient.invalidateQueries(["users"]);
                    queryClient.invalidateQueries(["roles"]);
                    onSuccess && onSuccess(newData);
                },
                ...props
            }
        );
    };

    const updateCustomer = (customer, { onSuccess, ...props } = {}) => {
        updateCustomerMutation.mutate(
            {
                method: "PATCH",
                url: `${keycloakapiUrls.customers()}${customer.id}/`,
                body: JSON.stringify({
                    name: customer.name,
                    permissions: customer.permissions,
                    ...(customer.rootUserId && { rootUserId: customer.rootUserId }),
                    ...(customer.rootUser && { rootUser: customer.rootUser })
                })
            },
            {
                onSuccess: newData => {
                    queryClient.setQueryData("customers", oldData => oldData.map(customer => (newData.id === customer.id ? newData : customer)));
                    queryClient.invalidateQueries(["users"]);
                    queryClient.invalidateQueries(["roles"]);
                    onSuccess && onSuccess(newData);
                },
                ...props
            }
        );
    };

    const deleteCustomer = (customer, { onSuccess, ...props } = {}) => {
        deleteCustomerMutation.mutate(
            {
                method: "DELETE",
                url: `${keycloakapiUrls.customers()}${customer.id}/`
            },
            {
                onSuccess: newData => {
                    queryClient.setQueryData("customers", oldData => oldData.filter(customer => customer.id !== newData));
                    queryClient.invalidateQueries(["users"]);
                    queryClient.invalidateQueries(["roles"]);
                    onSuccess && onSuccess();
                },
                ...props
            }
        );
    };

    return {
        customers,
        customersQueryState,
        createCustomer,
        updateCustomer,
        deleteCustomer,
        createCustomerMutation,
        updateCustomerMutation,
        deleteCustomerMutation
    };
};

export const useApps = queryProps => {
    return {
        apps: ["viisi", "analysisframework"]
    };
};
